import React, {
  FC,
  useEffect,
  useRef,
  useState,
  forwardRef,
  useMemo,
  useContext,
  memo,
} from 'react';
import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Marker } from '@googlemaps/markerclusterer';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  Pin,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import styled from '@emotion/styled';
import { renderToString } from 'react-dom/server';
import { css, keyframes } from '@emotion/react';
import {
  Close,
  ClusterPin,
  Dog,
  Heart,
  Parking,
  Pricing,
  Tick,
} from '@agria/theme/src/components/icon';
import { UiStateContext } from '@agria/theme/src/context/UiStateContext';
import { Input } from '@agria/theme/src/components/form/input/Input';
import { Blade } from '@agria/theme/src/components/blade/Blade';
import { Image } from '@agria/theme/src/components/Image/Image';
import { Link } from '@agria/theme/src/components/link/Link';
import { Typography } from '@agria/theme/src/components/typography/Typography';
import {
  CategoryInterface,
  FeatureInterface,
  LocationInterface,
  MapInterface,
} from './map.interface';
import { Html } from '../html/Html';

const StyledMapSearch = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const StyledMap = styled(Map)`
  border-radius: 4px;
  height: clamp(32.5rem, 30.7394rem + 7.5117vw, 37.5rem);
  width: 100%;
`;

const StyledMapKey = styled.div`
  ${({ theme: { media, space } }) => css`
    display: grid;
    grid-template-columns: 1fr;
    gap: ${space.fluidxxSmallToMedium};
    margin-left: auto;
    margin-right: auto;
    max-width: 600px;
    width: 100%;

    > div {
      display: grid;
      gap: ${space.fluidxxSmallToMedium};
      grid-template-columns: 1fr;
    }

    figure {
      align-items: center;
      display: flex;
      gap: ${space.fluidxxSmallToSmall};

      > div {
        flex-shrink: 0;
        max-width: 25px;
      }
    }

    @media (min-width: ${media.medium}) {
      grid-template-columns: auto 1fr;

      > div {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      }

      figure {
        > div {
          max-width: unset;
        }
      }
    }
  `};
`;

const StyledImage = styled(Image)`
  flex-shrink: 0;
`;

const StyledInput = styled(Input)`
  max-width: 400px;
  width: 100%;

  input {
    height: 60px;
  }
`;

const StyledMapBlade = styled.aside`
  ${({ theme: { media }, isClosing }) => {
    const slideIn = keyframes`
      from {
        transform: translateX(var(--containerMaxWidth));
      }
      to {
        transform: translateX(0);
      }
    `;

    const slideOut = keyframes`
      from {
        transform: translateX(0);
      }
      to {
        transform: translateX(var(--containerMaxWidth));
      }
    `;
    return css`
      --containerMaxWidth: 550px;
      animation-delay: 0.25s;
      animation-duration: 0.5s;
      animation-fill-mode: backwards;
      animation-name: ${isClosing ? slideOut : slideIn};
      animation-timing-function: ease-out;
      background: #fff;
      display: flex;
      flex-direction: column;
      height: 100%;
      inset: 0 0 auto auto;
      max-width: var(--containerMaxWidth);
      position: absolute;
      width: 100%;

      @media (max-width: ${media.medium}) {
        width: calc(100% - 15px);
      }
    `;
  }}
`;

const StyledMapHero = styled.div`
  display: grid;
  align-content: flex-end;
  position: relative;
`;

const StyledMapClose = styled.button`
  ${({ theme: { colors } }) => css`
    background: ${colors.brandOne100};
    clip-path: polygon(0 0, 100% 0, 100% 100%);
    display: flex;
    justify-content: flex-end;
    height: 100%;
    inset: 0 0 auto auto;
    max-height: 104px;
    max-width: 104px;
    padding: 10px;
    position: absolute;
    width: 100%;
    z-index: 1;

    svg {
      position: relative;
    }
  `};
`;

const StyledMapImage = styled(Image)`
  img {
    height: 100%;
    object-fit: contain;
    object-position: center;
    width: 100%;
  }
`;

const StyledMapBadge = styled(Image)`
  ${({ theme: { space } }) => css`
    height: auto;
    inset: auto auto ${space.fluidMedium} ${space.fluidMedium};
    position: absolute;
  `};
`;

const StyledMapHeader = styled.header`
  display: flex;
  flex-direction: column;
`;

const StyledMapTitle = styled.div`
  ${({ theme: { colors, space } }) => css`
    border-bottom: 1px solid ${colors.brandOne20};
    display: flex;
    flex-direction: column;
    gap: ${space.xxSmall};
    padding: 0 0 ${space.medium} 0;
  `};
`;

const StyledMapFeatures = styled.ul`
  ${({ theme: { colors, media, space } }) => css`
    border-bottom: 1px solid ${colors.brandOne20};
    display: flex;
    gap: ${space.fluidxxSmallToLarge};
    padding: ${space.small} 0;

    li {
      align-items: center;
      display: flex;
      gap: ${space.fluidXxxSmall};
    }

    @media (max-width: ${media.small}) {
      flex-direction: column;
    }
  `};
`;

const StyledMapContainer = styled.article`
  flex-grow: 1;
  overflow-y: auto;
  position: relative;
`;

const StyledMapBody = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: ${space.medium};
    padding: ${space.fluidMedium};
  `};
`;

const StyledMapFooter = styled.footer`
  ${({ theme: { colors, space } }) => css`
    background: ${colors.utilityBackground};
    width: 100%;

    a {
      max-width: max-content;
      padding: ${space.fluidMedium};
    }
  `};
`;

const MapSearch: FC<{
  onPlaceSelect: (place: google.maps.places.PlaceResult) => void;
}> = ({ onPlaceSelect }) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['geometry', 'name', 'formatted_address', 'icon'],
      componentRestrictions: { country: 'gb' },
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener('place_changed', () => {
      onPlaceSelect(placeAutocomplete.getPlace());
    });
  }, [onPlaceSelect, placeAutocomplete]);

  return (
    <StyledMapSearch>
      <StyledInput
        id="search"
        label="Search for a place, town or postcode in the UK"
        large
        placeholder="Search"
        ref={inputRef}
        type="search"
        autocomplete="off"
      />
    </StyledMapSearch>
  );
};

const CustomCluster = (categoryColour: string) => ({
  render({ count, position }: Cluster): Marker {
    const max: number = google.maps.Marker.MAX_ZINDEX;
    const zIndex: number = max + count;
    const countDisplay = count.toString().padStart(2, '0');
    const clusterPin = renderToString(<ClusterPin color={categoryColour} />);
    const clusterUrl = `data:image/svg+xml,${encodeURIComponent(clusterPin)}`;

    return new google.maps.Marker({
      icon: {
        labelOrigin: new google.maps.Point(17, 22),
        scaledSize: new google.maps.Size(40, 49),
        url: clusterUrl,
      },
      label: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '14px',
        fontWeight: 'bold',
        text: countDisplay,
      },
      optimized: true,
      position,
      zIndex,
    });
  },
});

const IconMap: { [key: string]: React.ComponentType<any> } = {
  Dog,
  Heart,
  Parking,
  Entry: Pricing,
  Tick,
};

const FeatureIcon: FC<{ iconName: string }> = ({ iconName }) => {
  const normalizedIcon = iconName.replace(/\s+/g, '');
  const dynamicIconKey =
    normalizedIcon.charAt(0).toUpperCase() + normalizedIcon.slice(1);
  const Icon = IconMap[dynamicIconKey as keyof typeof IconMap];

  if (!Icon) {
    console.error(`Icon "${iconName}" is not defined in IconMap`);
    return null;
  }

  return <Icon />;
};

const MapBlade: FC<LocationInterface> = ({
  categoryBadge,
  categoryName,
  locationDescription,
  locationFeatures,
  locationImage,
  locationLink,
  locationLongAddress,
  locationName,
  onBladeClose,
  locationOpeningTimes,
  locationShortAddress,
  locationStartTime,
  isClosing,
}) => (
  <StyledMapBlade isClosing={isClosing}>
    <StyledMapClose aria-label="close" onClick={onBladeClose}>
      <Close color="utilityWhite" size="large" />
    </StyledMapClose>
    <StyledMapContainer>
      <StyledMapHero>
        {locationImage && <StyledMapImage src={locationImage} />}
        {categoryBadge && (
          <StyledMapBadge src={categoryBadge} alt={categoryName} />
        )}
      </StyledMapHero>
      <StyledMapBody>
        <StyledMapHeader>
          {(locationName || locationShortAddress) && (
            <StyledMapTitle>
              {locationName && (
                <Typography component="h3" variant="headingLarge">
                  {locationName}
                </Typography>
              )}
              {locationShortAddress && (
                <Typography component="h4" variant="headingMedium">
                  {locationShortAddress}
                </Typography>
              )}
            </StyledMapTitle>
          )}
          {locationFeatures && locationFeatures.length >= 1 && (
            <StyledMapFeatures>
              {locationFeatures.map((feature: FeatureInterface, index) => (
                <li key={index}>
                  {feature.featureIcon && (
                    <FeatureIcon iconName={feature.featureIcon} />
                  )}
                  <Typography variant="bodySmall">
                    {feature.featureText}
                  </Typography>
                </li>
              ))}
            </StyledMapFeatures>
          )}
        </StyledMapHeader>
        {locationDescription && (
          <Typography variant="bodyMedium">
            <Html handleNestedSpacing>{locationDescription}</Html>
          </Typography>
        )}
        {locationLongAddress && (
          <div>
            <Typography variant="bodyMediumBold">Address</Typography>
            <Typography variant="bodyMedium">{locationLongAddress}</Typography>
          </div>
        )}
        {locationOpeningTimes && (
          <div>
            <Typography variant="bodyMediumBold">Open Hours</Typography>
            <Typography variant="bodyMedium">{locationOpeningTimes}</Typography>
          </div>
        )}
        {locationStartTime && (
          <div>
            <Typography variant="bodyMediumBold">Start Time</Typography>
            <Typography variant="bodyMedium">{locationStartTime}</Typography>
          </div>
        )}
      </StyledMapBody>
    </StyledMapContainer>
    {locationLink && (
      <StyledMapFooter>
        <Link
          label={locationLink.linkLabel}
          target={locationLink.linkTarget}
          url={locationLink.linkUrl}
        />
      </StyledMapFooter>
    )}
  </StyledMapBlade>
);

const MarkerPin = forwardRef<LocationInterface>(
  (
    {
      id,
      categoryBadge,
      categoryPin,
      categoryName,
      locationDescription,
      locationFeatures,
      locationImage,
      locationLink,
      locationMarker,
      locationLongAddress,
      locationName,
      locationOpeningTimes,
      locationShortAddress,
      locationStartTime,
    },
    ref
  ) => {
    const {
      setActiveBlade,
      activeBladeId,
      setActiveBladeId,
      setActiveBladeTriggerEl,
    } = useContext(UiStateContext);

    const bladeTriggerRef = useRef<HTMLButtonElement | null>(null);
    const [isClosing, setIsClosing] = useState(false);
    const memoPin = useMemo(() => <Image src={categoryPin} />, [categoryPin]);

    const handleOpenBlade = () => {
      setActiveBlade('map');
      setActiveBladeId(id);
      setActiveBladeTriggerEl(bladeTriggerRef);
    };

    const handleBladeClose = () => {
      setIsClosing(true);
    };

    useEffect(() => {
      if (isClosing) {
        const timer = setTimeout(() => {
          setActiveBladeId(undefined);
          setActiveBlade(null);
          setActiveBladeTriggerEl(null);
          setIsClosing(false);
        }, 650);

        return () => clearTimeout(timer);
      }
    }, [setActiveBlade, setActiveBladeId, setActiveBladeTriggerEl, isClosing]);

    return (
      <>
        <AdvancedMarker
          clickable
          onClick={handleOpenBlade}
          position={locationMarker}
          ref={ref}
        >
          {categoryPin ? (
            memoPin
          ) : (
            <Pin
              background="#e5eaee"
              borderColor="#02315B"
              glyphColor="#02315B"
            />
          )}
        </AdvancedMarker>
        {/* prop drilling!! 🤮 */}

        {activeBladeId === id && (
          <Blade customBlade id="map" renderChildrenOnOpen width="full">
            <MapBlade
              categoryBadge={categoryBadge}
              categoryName={categoryName}
              locationDescription={locationDescription}
              locationFeatures={locationFeatures}
              locationImage={locationImage}
              locationLink={locationLink}
              locationLongAddress={locationLongAddress}
              locationName={locationName}
              onBladeClose={handleBladeClose}
              locationOpeningTimes={locationOpeningTimes}
              locationShortAddress={locationShortAddress}
              locationStartTime={locationStartTime}
              isOpen={activeBladeId === id}
              isClosing={isClosing}
            />
          </Blade>
        )}
      </>
    );
  }
);
// this is deprecated
MarkerPin.displayName = 'MarkerPin';

const MapMarkers: FC<CategoryInterface> = ({
  categoryBadge,
  categoryColour = '#02315B',
  categoryPin,
  categoryName,
  categoryLocations,
}) => {
  const Map = useMap();
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const [clusterMarkers, setClusterMarkers] = useState<Marker[]>([]);
  const markerCluster = useRef<MarkerClusterer | null>(null);
  const clusterBadge = useMemo(
    () => CustomCluster(categoryColour),
    [categoryColour]
  );

  useEffect(() => {
    if (!markerCluster.current) {
      markerCluster.current = new MarkerClusterer({
        map: Map,
        markers: [],
        renderer: clusterBadge,
      });
    }
  }, [clusterBadge, Map]);

  useEffect(() => {
    if (
      Map &&
      markerCluster.current &&
      markers &&
      Object.values(markers).length
    ) {
      google.maps.event.addListener(Map, 'idle', () => {
        const newMarkers = Object.values(markers);

        const markersToAdd = newMarkers.filter(
          (newMarker) => !clusterMarkers.includes(newMarker)
        );

        const markersToRemove = clusterMarkers.filter(
          (currentMarker) => !newMarkers.includes(currentMarker)
        );

        markersToRemove.forEach((marker) => {
          markerCluster.current.removeMarker(marker);

          setClusterMarkers((prevMarkers) =>
            prevMarkers.filter((m) => m !== marker)
          );
        });

        markersToAdd.forEach((marker) => {
          markerCluster.current.addMarker(marker);

          setClusterMarkers((prevMarkers) => [...prevMarkers, marker]);
        });
      });
    }
  }, [clusterMarkers, markers, Map]);

  const setMarkerRef = (marker: Marker | null, key: string) => {
    if (marker && markers[key]) return;

    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if (marker) {
        return { ...prev, [key]: marker };
      }

      const newMarkers = { ...prev };

      delete newMarkers[key];

      return newMarkers;
    });
  };

  return categoryLocations.map((location, index) => (
    <MarkerPin
      key={index}
      categoryBadge={categoryBadge}
      categoryName={categoryName}
      categoryPin={categoryPin}
      id={`${categoryName}-${index}`}
      locationDescription={location?.locationDescription}
      locationFeatures={location?.locationFeatures}
      locationImage={location?.locationImage?.image.localFile}
      locationLink={location?.locationLink}
      locationLongAddress={location?.locationLongAddress}
      locationMarker={location?.locationMarker}
      locationName={location?.locationName}
      locationOpeningTimes={location?.locationOpeningTimes}
      locationShortAddress={location?.locationShortAddress}
      locationStartTime={location?.locationStartTime}
      ref={(marker: any) => setMarkerRef(marker, `${index}`)}
    />
  ));
};

const MapHandler: FC<{ place: google.maps.places.PlaceResult | null }> = ({
  place,
}) => {
  const Map = useMap();

  useEffect(() => {
    if (!Map || !place) return;

    if (place && place.geometry?.viewport) {
      Map.fitBounds(place.geometry?.viewport);
    }
  }, [Map, place]);

  return null;
};

const MapKey: FC<{ categories: CategoryInterface[] }> = ({ categories }) => (
  <StyledMapKey>
    <Typography component="h3" variant="bodyLargeBold">
      Key
    </Typography>
    <div>
      {categories.map((cat, index) => (
        <figure key={index}>
          <StyledImage src={cat.categoryPin?.image.localFile} />
          <Typography variant="bodySmall">{cat.categoryName}</Typography>
        </figure>
      ))}
    </div>
  </StyledMapKey>
);

const MapContainer: FC<MapInterface> = ({ ...rest }) => {
  const {
    categories,
    defaultCentre = { lat: 52.5394, lng: -1.7576 },
    defaultZoom = 7,
    disableUi = false,
  } = rest.locationSet;
  const apiKey = `${process.env.GATSBY_GOOGLE_MAPS_API}`;

  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);

  return (
    <>
      <APIProvider apiKey={apiKey}>
        <MapSearch onPlaceSelect={setSelectedPlace} />
        <StyledMap
          defaultCenter={defaultCentre}
          defaultZoom={defaultZoom}
          disableDefaultUI={disableUi}
          gestureHandling="cooperative"
          mapId="bf51a910020fa25a"
          minZoom={6}
          maxZoom={16}
        >
          {categories &&
            categories.length >= 1 &&
            categories.map((cat: CategoryInterface, index: any) => (
              <MapMarkers
                key={index}
                categoryBadge={cat?.categoryBadge?.image.localFile}
                categoryColour={cat?.categoryColour}
                categoryPin={cat?.categoryPin?.image.localFile}
                categoryLocations={cat?.categoryLocations}
                categoryName={cat?.categoryName}
              />
            ))}
        </StyledMap>
        <MapHandler place={selectedPlace} />
      </APIProvider>

      {categories && categories.length >= 1 && (
        <MapKey categories={categories} />
      )}
    </>
  );
};

export { MapContainer };
